import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ListMusic, Plus, Trash2 } from 'lucide-react';
import { Button, Input, IconButton } from '@/components/ui';
import { DjSet, getSets, createSet, deleteSet } from '@/services/sets.api';
import {
  formatSetDuration,
  setDurationRatio,
  isSetDurationDanger,
  SET_MAX_DURATION_LABEL,
} from './setDuration';

const Sets: React.FC = () => {
  const [sets, setSets] = useState<DjSet[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const loadSets = useCallback(async () => {
    setIsLoading(true);
    try {
      setSets(await getSets());
    } catch {
      console.error('Failed to load sets');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSets();
  }, [loadSets]);

  const handleCreate = async () => {
    const trimmed = newName.trim();
    if (trimmed.length === 0) return;
    setIsCreating(true);
    try {
      const created = await createSet(trimmed);
      setSets(prev => [created, ...prev]);
      setNewName('');
    } catch {
      console.error('Failed to create set');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async (set: DjSet) => {
    if (!window.confirm(`Delete set "${set.name}"?`)) return;
    try {
      await deleteSet(set.id);
      setSets(prev => prev.filter(item => item.id !== set.id));
    } catch {
      console.error('Failed to delete set');
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <ListMusic className="w-7 h-7 text-meradio-primary" />
        <h1 className="text-3xl font-bold text-meradio-dark dark:text-text-dark-primary">Sets</h1>
      </div>

      <div className="flex items-center gap-2 mb-8 max-w-md">
        <Input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleCreate();
          }}
          placeholder="New set name"
          aria-label="New set name"
          maxLength={60}
        />
        <Button
          variant="primary"
          onClick={handleCreate}
          isLoading={isCreating}
          disabled={newName.trim().length === 0}
        >
          <Plus className="w-4 h-4" />
          New set
        </Button>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-36 rounded-xl bg-neutral-200 dark:bg-bg-dark-secondary animate-pulse" />
          ))}
        </div>
      ) : sets.length === 0 ? (
        <div className="text-center py-16">
          <ListMusic className="w-16 h-16 text-neutral-300 dark:text-text-dark-tertiary mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-neutral-600 dark:text-text-dark-primary mb-2">
            No sets yet
          </h3>
          <p className="text-neutral-500 dark:text-text-dark-secondary">
            Sequence your records into a three-hour set.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {sets.map((set) => {
            const ratio = setDurationRatio(set.totalDurationSeconds);
            const danger = isSetDurationDanger(set.totalDurationSeconds);
            return (
              <div
                key={set.id}
                className="group relative rounded-xl bg-white dark:bg-bg-dark-secondary border border-meradio-light dark:border-border-dark p-5 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="absolute top-3 right-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <IconButton
                    onClick={() => handleDelete(set)}
                    variant="ghost"
                    size="sm"
                    aria-label="Delete set"
                    className="text-meradio-dark-light dark:text-text-dark-secondary hover:text-danger-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </IconButton>
                </div>

                <Link to={`/sets/${set.id}`} className="block">
                  <div className="w-12 h-12 rounded-lg bg-meradio-primary/10 flex items-center justify-center mb-4">
                    <ListMusic className="w-6 h-6 text-meradio-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-meradio-dark dark:text-text-dark-primary truncate pr-10">
                    {set.name}
                  </h3>
                  <p className="text-sm text-meradio-dark-light dark:text-text-dark-secondary mt-1">
                    {set.entryCount} {set.entryCount === 1 ? 'track' : 'tracks'}
                  </p>

                  <div className="mt-4">
                    <div className="flex items-center justify-between text-xs tabular-nums mb-1">
                      <span
                        className={
                          danger
                            ? 'text-danger-600 dark:text-danger-400 font-medium'
                            : 'text-meradio-dark-light dark:text-text-dark-secondary'
                        }
                      >
                        {formatSetDuration(set.totalDurationSeconds)} / {SET_MAX_DURATION_LABEL}
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-neutral-200 dark:bg-bg-dark-tertiary overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          danger ? 'bg-danger-500' : 'bg-meradio-primary'
                        }`}
                        style={{ width: `${ratio * 100}%` }}
                      />
                    </div>
                  </div>
                </Link>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Sets;
